import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import Modal from '../Modal.jsx';
import QuestionForm from '../../components/Questions/QuestionForm.jsx';
import Loading from '../Loading.jsx';
import HeaderContainer from '../HeaderContainer.jsx';
import RightSidebar from '../RightSidebar.jsx';
import AIChat from '../../components/MediBot/AIChat.jsx';

import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  TableButton,
} from '../../components/Custom-UI/Tables/Elements.jsx';
import Checkbox from '../Form/Checkbox.jsx';
import ImageComponent from '../ImageComponent.jsx';
import { deleteQuestionEvaluation, patchEvaluation } from '../../models/evaluations.js';
import { useGetQuestionByEvaluation, patchQuestionEvaluation, getQuestionByEvaluation } from '../../models/questions';

const correctAnswerVF = { 0: 'F', 1: 'V' };

const EvaluationQuestionsTable = ({ evaluationId, course, learningUnit, evaluation }) => {
  const navigate = useNavigate();

  const [groupedQuestions, setGroupedQuestions] = useState({});
  const [maxTableNumber, setMaxTableNumber] = useState(0);
  const [totalUnassignedQuestions, setTotalUnassignedQuestions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [questionsData, questionsDataLoading] = useGetQuestionByEvaluation(evaluationId);
  const [showImages, setShowImages] = useState(true);
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTitle, setChatTitle] = useState('');
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const [method, setMethod] = useState('POST');
  const [collapsedTables, setCollapsedTables] = useState({});
  const [draggedQuestion, setDraggedQuestion] = useState(null);
  const [visibleLineId, setVisibleLineId] = useState(null); // Format: "table-{tableKey}-line-{index}"
  const dropTargetRef = useRef({ table: null, targetIndex: null });

  const itemsBreadcrumb = {
    items: [
      { name: 'Cursos', href: '/courses' },
      { name: 'Unidades de aprendizaje', href: `/courses/${course?.id}/learning_units` },
      {
        name: 'Evaluaciones',
        href: `/courses/${course?.id}/${learningUnit?.id}/evaluations`,
      },
      {
        name: `Evaluación ${evaluation?.name} del curso ${course?.name} de la unidad ${learningUnit?.name}`,
        href: '',
      },
    ],
    maxItems: 4,
  };

  const actions = [
    {
      title: 'Gestionar preguntas',
      icon: <SmartToyOutlinedIcon />,
      handleClick: () => {
        setChatTitle('Gestionar preguntas de la evaluación');
        setChatOpen(true);
      },
    },
    {
      title: 'Importar preguntas',
      icon: <AddIcon />,
      handleClick: () =>
        navigate(`/courses/${course?.id}/${learningUnit?.id}/${evaluationId}/import_questions`),
    },
    {
      title: 'Añadir del banco de preguntas',
      icon: <AddIcon />,
      handleClick: () =>
        navigate(`/courses/${course?.id}/${learningUnit?.id}/${evaluationId}/select_questions`),
    },
    {
      title: 'Crear pregunta',
      icon: <AddIcon />,
      handleClick: () => {
        setQuestionToEdit(null);
        setMethod('POST');
        setOpen(true);
      },
    },
    {},
  ];

  const fetchQuestions = useCallback(async () => {
    try {
      const freshQuestions = await getQuestionByEvaluation(evaluationId);

      // Calculate max table number
      const tableNumbers = freshQuestions
        .map(q => q.table)
        .filter(t => t !== null && !isNaN(t));
      const maxTable = tableNumbers.length > 0 ? Math.max(...tableNumbers) : 0;
      setMaxTableNumber(maxTable);

      // Calculate total unassigned questions
      const unassignedCount = freshQuestions.filter(q => !q.table).length;
      setTotalUnassignedQuestions(unassignedCount);

      // Sort all questions by table and order first
      const sortedQuestions = [...freshQuestions].sort((a, b) => {
        const tableA = a.table || 'Sin mesa';
        const tableB = b.table || 'Sin mesa';

        // Sort by table first
        if (tableA === 'Sin mesa' && tableB !== 'Sin mesa') return 1;
        if (tableB === 'Sin mesa' && tableA !== 'Sin mesa') return -1;
        if (tableA !== tableB) {
          return parseInt(tableA) - parseInt(tableB);
        }

        // Then by order within the same table
        return a.order - b.order;
      });

      // Group paginated questions by table
      const grouped = sortedQuestions.reduce((acc, question) => {
        const tableKey = question.table || 'Sin mesa';
        if (!acc[tableKey]) {
          acc[tableKey] = [];
        }
        acc[tableKey].push(question);
        return acc;
      }, {});

      setGroupedQuestions(grouped);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [evaluationId]);

  const handleDelete = async questionId => {
    const confirmation = window.confirm(
      '¿Estás seguro de eliminar esta pregunta de la evaluación?'
    );
    if (confirmation) {
      const response = await deleteQuestionEvaluation(questionId, evaluationId);
      if (response.status === 200 || response.status === 201) {
        alert('Pregunta eliminada.');
        await fetchQuestions();

        // After fetching, update evaluation stats
        const freshQuestions = await getQuestionByEvaluation(evaluationId);

        // Calculate max table number
        const tableNumbers = freshQuestions
          .map(q => q.table)
          .filter(t => t !== null && !isNaN(t));
        const maxTable = tableNumbers.length > 0 ? Math.max(...tableNumbers) : 0;

        // Calculate questions per table (max questions in any table)
        const questionsPerTableCounts = {};
        freshQuestions.forEach(q => {
          if (q.table) {
            questionsPerTableCounts[q.table] = (questionsPerTableCounts[q.table] || 0) + 1;
          }
        });
        const maxQuestionsPerTable = Object.keys(questionsPerTableCounts).length > 0
          ? Math.max(...Object.values(questionsPerTableCounts))
          : 0;

        // Update evaluation
        await patchEvaluation(evaluationId, {
          tables_per_evaluation: maxTable,
          questions_per_table: maxQuestionsPerTable,
        });
      } else {
        alert('Error al eliminar la pregunta.');
      }
    }
  };

  const toggleTableCollapse = (tableKey) => {
    setCollapsedTables(prev => ({
      ...prev,
      [tableKey]: !prev[tableKey]
    }));
  };

  const handleDragStart = (e, question) => {
    setDraggedQuestion(question);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedQuestion(null);
    setVisibleLineId(null);
    dropTargetRef.current = { table: null, targetIndex: null };
  };

  const handleRowHover = (e, tableKey, question, isHeader = false) => {
    if (!draggedQuestion) return;

    e.stopPropagation(); // Prevent event propagation to avoid multiple lines

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const rowMiddle = rect.top + rect.height / 2;
    const isTop = mouseY < rowMiddle;

    const sortedQuestions = [...(groupedQuestions[tableKey] || [])].sort((a, b) => a.order - b.order);

    if (isHeader) {
      // Hovering over table header - show line after header (before first question)
      setVisibleLineId(`table-${tableKey}-line-0`);
      dropTargetRef.current = {
        table: tableKey,
        targetIndex: 0  // Insert at beginning
      };
    } else {
      const questionIndex = sortedQuestions.findIndex(q => q.id === question.id);

      if (isTop) {
        // Show line above this question - insert before it
        setVisibleLineId(`table-${tableKey}-line-${questionIndex}`);
        dropTargetRef.current = {
          table: tableKey,
          targetIndex: questionIndex
        };
      } else {
        // Show line below this question - insert after it
        setVisibleLineId(`table-${tableKey}-line-${questionIndex + 1}`);
        dropTargetRef.current = {
          table: tableKey,
          targetIndex: questionIndex + 1
        };
      }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent multiple drop handlers

    if (!draggedQuestion || !dropTargetRef.current.table) return;

    const sourceTable = draggedQuestion.table || 'Sin mesa';
    const targetTable = dropTargetRef.current.table;
    const targetIndex = dropTargetRef.current.targetIndex;

    // Unified logic for both same-table and cross-table moves
    const allTables = { ...groupedQuestions };
    const affectedTables = new Set([sourceTable, targetTable]);

    // 1. Remove from source table
    const sourceQuestions = [...(allTables[sourceTable] || [])].sort((a, b) => a.order - b.order);
    const withoutDragged = sourceQuestions.filter(q => q.id !== draggedQuestion.id);
    const sourceUpdated = withoutDragged.map((q, idx) => ({ ...q, order: idx + 1 }));
    allTables[sourceTable] = sourceUpdated;

    // 2. Insert into target table at target index
    const targetQuestions = sourceTable === targetTable
      ? sourceUpdated  // If same table, use the already updated list
      : [...(allTables[targetTable] || [])].sort((a, b) => a.order - b.order);

    const updatedDraggedQuestion = {
      ...draggedQuestion,
      table: targetTable === 'Sin mesa' ? null : parseInt(targetTable),
    };

    targetQuestions.splice(targetIndex, 0, updatedDraggedQuestion);
    const targetUpdated = targetQuestions.map((q, idx) => ({ ...q, order: idx + 1 }));
    allTables[targetTable] = targetUpdated;

    // 3. Update state
    setGroupedQuestions(allTables);
    const allQuestions = Object.values(allTables).flat();

    // Recalculate maxTableNumber based on actual questions (not just when creating new tables)
    const tableNumbers = allQuestions
      .map(q => q.table)
      .filter(t => t !== null && !isNaN(t));
    const newMaxTableNumber = tableNumbers.length > 0 ? Math.max(...tableNumbers) : 0;
    setMaxTableNumber(newMaxTableNumber);

    // Update totalUnassignedQuestions
    const newUnassignedCount = allQuestions.filter(q => !q.table).length;
    setTotalUnassignedQuestions(newUnassignedCount);

    // Calculate questions per table (max questions in any table)
    const questionsPerTableCounts = {};
    allQuestions.forEach(q => {
      if (q.table) {
        questionsPerTableCounts[q.table] = (questionsPerTableCounts[q.table] || 0) + 1;
      }
    });
    const maxQuestionsPerTable = Object.keys(questionsPerTableCounts).length > 0
      ? Math.max(...Object.values(questionsPerTableCounts))
      : 0;

    // 4. Update backend - only update affected tables
    try {
      const updates = [];
      affectedTables.forEach(tableKey => {
        const tableQuestions = allTables[tableKey] || [];
        tableQuestions.forEach(q => {
          updates.push(
            patchQuestionEvaluation({
              eval_id: evaluationId,
              question_id: q.id,
              table: tableKey === 'Sin mesa' ? null : parseInt(tableKey),
              order: q.order,
            })
          );
        });
      });
      await Promise.all(updates);

      // Update evaluation with new table count and questions per table
      await patchEvaluation(evaluationId, {
        tables_per_evaluation: newMaxTableNumber,
        questions_per_table: maxQuestionsPerTable,
      });
    } catch (error) {
      // Revert on error
      const originalGrouped = questionsData.reduce((acc, question) => {
        const tableKey = question.table || 'Sin mesa';
        if (!acc[tableKey]) acc[tableKey] = [];
        acc[tableKey].push(question);
        return acc;
      }, {});
      setGroupedQuestions(originalGrouped);

      // Recalculate maxTableNumber on error
      const tableNumbers = questionsData
        .map(q => q.table)
        .filter(t => t !== null && !isNaN(t));
      const maxTable = tableNumbers.length > 0 ? Math.max(...tableNumbers) : 0;
      setMaxTableNumber(maxTable);

      // Recalculate unassigned count on error
      const unassignedCount = questionsData.filter(q => !q.table).length;
      setTotalUnassignedQuestions(unassignedCount);
    }

    setDraggedQuestion(null);
    setVisibleLineId(null);
    dropTargetRef.current = { table: null, targetIndex: null };
  };

  useEffect(() => {
    if (questionsData && !questionsDataLoading) {

      // Calculate max table number
      const tableNumbers = questionsData
        .map(q => q.table)
        .filter(t => t !== null && !isNaN(t));
      const maxTable = tableNumbers.length > 0 ? Math.max(...tableNumbers) : 0;
      setMaxTableNumber(maxTable);

      // Calculate total unassigned questions
      const unassignedCount = questionsData.filter(q => !q.table).length;
      setTotalUnassignedQuestions(unassignedCount);

      // Sort all questions by table and order first
      const sortedQuestions = [...questionsData].sort((a, b) => {
        const tableA = a.table || 'Sin mesa';
        const tableB = b.table || 'Sin mesa';

        // Sort by table first
        if (tableA === 'Sin mesa' && tableB !== 'Sin mesa') return 1;
        if (tableB === 'Sin mesa' && tableA !== 'Sin mesa') return -1;
        if (tableA !== tableB) {
          return parseInt(tableA) - parseInt(tableB);
        }

        // Then by order within the same table
        return a.order - b.order;
      });

      // Group paginated questions by table
      const grouped = sortedQuestions.reduce((acc, question) => {
        const tableKey = question.table || 'Sin mesa';
        if (!acc[tableKey]) {
          acc[tableKey] = [];
        }
        acc[tableKey].push(question);
        return acc;
      }, {});

      setGroupedQuestions(grouped);
      setLoading(false);
    }
  }, [questionsData, questionsDataLoading]);

  const closeChat = useCallback(() => {
    setChatOpen(false);
    setTimeout(() => {
      try {
        navigate(0);
      } catch (error) {
        window.location.reload();
      }
    }, 300);
  }, [navigate]);

  return (
    <div className='antialiased font-sans'>
      <RightSidebar open={chatOpen} onClose={closeChat} title={chatTitle}>
        <AIChat onClose={closeChat} open={chatOpen} />
      </RightSidebar>
      <Modal open={open} setOpen={setOpen}>
        <QuestionForm
          setOpen={setOpen}
          method={method}
          question={questionToEdit}
          unit_id={learningUnit?.id}
          eval_id={evaluationId}
        />
      </Modal>
      <div className='w-full divide-y divide-primary'>
        <HeaderContainer breadcrumb={itemsBreadcrumb} actions={actions} />
        <div className='py-8 px-4 sm:px-8 min-w-full w-full'>
          <div className='flex justify-between items-center mb-4'>
            <Checkbox
              label='Ver imágenes'
              defaultChecked
              onChange={() => setShowImages(!showImages)}
            />
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader style={{ width: '40px' }} />
                {showImages ? <TableHeader>Imagen</TableHeader> : null}
                <TableHeader>Pregunta</TableHeader>
                <TableHeader>Respuesta correcta</TableHeader>
                <TableHeader>Tipo</TableHeader>
                <TableHeader>Tiempo (seg.)</TableHeader>
                <TableHeader>Mesa</TableHeader>
                <TableHeader>Orden</TableHeader>
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {questionsDataLoading || loading ? (
                <TableRow>
                  <TableCell colSpan='9'>
                    <div className='flex justify-center items-center h-full'>
                      <Loading />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {/* Render all tables from 1 to maxTableNumber (including empty ones) */}
                  {Array.from({ length: maxTableNumber }, (_, i) => i + 1).map((tableNumber) => {
                    const tableKey = String(tableNumber);
                    const tableQuestions = groupedQuestions[tableKey] || [];
                    const sortedQuestions = [...tableQuestions].sort((a, b) => a.order - b.order);

                    return (
                      <React.Fragment key={tableKey}>
                        {/* Table header row */}
                        <TableRow
                          className="bg-blue-50 hover:bg-blue-100"
                          onDragOver={(e) => {
                            e.preventDefault();
                            handleRowHover(e, tableKey, null, true);
                          }}
                          onDrop={handleDrop}
                        >
                          <TableCell colSpan={showImages ? 9 : 8} className='font-bold'>
                            <div className='flex items-center gap-3'>
                              <button
                                onClick={() => toggleTableCollapse(tableKey)}
                                className='p-1 hover:bg-blue-200 rounded'
                              >
                                {collapsedTables[tableKey] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                              </button>
                              <span className='text-lg'>Mesa {tableKey}</span>
                              <span className='text-sm text-gray-600'>
                                ({tableQuestions.length} pregunta{tableQuestions.length !== 1 ? 's' : ''})
                                {tableQuestions.length === 0 && <span className='italic'> - vacía</span>}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                        {/* Drop line after header */}
                        <tr>
                          <td colSpan={showImages ? 9 : 8} className="p-0">
                            <div
                              className={`h-1 transition-all ${visibleLineId === `table-${tableKey}-line-0`
                                ? 'bg-blue-500 shadow-lg'
                                : 'bg-transparent'
                                }`}
                            />
                          </td>
                        </tr>
                        {/* Questions rows */}
                        {!collapsedTables[tableKey] && sortedQuestions.map((question, index) => (
                          <React.Fragment key={question.id}>
                            <TableRow
                              draggable
                              onDragStart={(e) => handleDragStart(e, question)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => {
                                e.preventDefault();
                                handleRowHover(e, tableKey, question, false);
                              }}
                              onDrop={handleDrop}
                              className={`cursor-move ${draggedQuestion?.id === question.id ? 'opacity-50' : ''}`}
                              style={{ cursor: 'grab' }}
                            >
                              <TableCell style={{ width: '40px', padding: '8px' }}>
                                <DragIndicatorIcon className='text-gray-400' />
                              </TableCell>
                              {showImages ? (
                                <TableCell>
                                  {question.image_id ? (
                                    <ImageComponent image_id={question.image_id} />
                                  ) : (
                                    'Sin imagen asociada'
                                  )}
                                </TableCell>
                              ) : null}
                              <TableCell>{question.content}</TableCell>
                              <TableCell>
                                {question.question_type === 'ALTERNATIVAS'
                                  ? question.possible_answers[question.correct_answer]
                                  : question.question_type === 'V/F'
                                    ? correctAnswerVF[question.correct_answer]
                                    : question.possible_answers.join(' | ')}
                              </TableCell>
                              <TableCell>{question.question_type}</TableCell>
                              <TableCell>{question.time_to_answer}</TableCell>
                              <TableCell>{question.table}</TableCell>
                              <TableCell>{question.order}</TableCell>
                              <TableCell>
                                <div className='flex w-full justify-between gap-2 items-center'>
                                  <TableButton
                                    variant={'edit'}
                                    onClick={() => {
                                      setQuestionToEdit(question);
                                      setMethod('PATCH');
                                      setOpen(true);
                                    }}>
                                    <EditOutlinedIcon />
                                  </TableButton>
                                  <TableButton variant={'delete'} onClick={() => handleDelete(question.id)}>
                                    <DeleteOutlinedIcon color='red' />
                                  </TableButton>
                                </div>
                              </TableCell>
                            </TableRow>
                            {/* Drop line after question */}
                            <tr>
                              <td colSpan={showImages ? 9 : 8} className="p-0">
                                <div
                                  className={`h-1 transition-all ${visibleLineId === `table-${tableKey}-line-${index + 1}`
                                    ? 'bg-blue-500 shadow-lg'
                                    : 'bg-transparent'
                                    }`}
                                />
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  {/* Always visible: New table row (maxTableNumber + 1) */}
                  <React.Fragment key={`new-table-${maxTableNumber + 1}`}>
                    <TableRow
                      className="bg-green-50 hover:bg-green-100"
                      onDragOver={(e) => {
                        e.preventDefault();
                        handleRowHover(e, String(maxTableNumber + 1), null, true);
                      }}
                      onDrop={handleDrop}
                    >
                      <TableCell colSpan={showImages ? 8 : 7} className='font-bold'>
                        <div className='flex items-center gap-3'>
                          <button
                            onClick={() => toggleTableCollapse(String(maxTableNumber + 1))}
                            className='p-1 hover:bg-green-200 rounded'
                          >
                            {collapsedTables[String(maxTableNumber + 1)] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                          </button>
                          <span className='text-lg'>Mesa {maxTableNumber + 1}</span>
                          <span className='text-sm text-gray-600 italic'>
                            (Nueva mesa - arrastra preguntas aquí)
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    <tr>
                      <td colSpan={showImages ? 8 : 7} className="p-0">
                        <div
                          className={`h-1 transition-all ${visibleLineId === `table-${maxTableNumber + 1}-line-0`
                            ? 'bg-blue-500 shadow-lg'
                            : 'bg-transparent'
                            }`}
                        />
                      </td>
                    </tr>
                  </React.Fragment>

                  {/* Always visible: Sin mesa row */}
                  <React.Fragment key="sin-mesa-always">
                    <TableRow
                      className="bg-gray-50 hover:bg-gray-100"
                      onDragOver={(e) => {
                        e.preventDefault();
                        handleRowHover(e, 'Sin mesa', null, true);
                      }}
                      onDrop={handleDrop}
                    >
                      <TableCell colSpan={showImages ? 8 : 7} className='font-bold'>
                        <div className='flex items-center gap-3'>
                          <button
                            onClick={() => toggleTableCollapse('Sin mesa')}
                            className='p-1 hover:bg-gray-200 rounded'
                          >
                            {collapsedTables['Sin mesa'] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                          </button>
                          <span className='text-lg'>Sin mesa</span>
                          <span className='text-sm text-gray-600 italic'>
                            ({totalUnassignedQuestions} pregunta{totalUnassignedQuestions !== 1 ? 's' : ''})
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    <tr>
                      <td colSpan={showImages ? 8 : 7} className="p-0">
                        <div
                          className={`h-1 transition-all ${visibleLineId === 'table-Sin mesa-line-0'
                            ? 'bg-blue-500 shadow-lg'
                            : 'bg-transparent'
                            }`}
                        />
                      </td>
                    </tr>
                    {/* Questions without table */}
                    {!collapsedTables['Sin mesa'] && groupedQuestions['Sin mesa'] &&
                      [...groupedQuestions['Sin mesa']].sort((a, b) => a.order - b.order).map((question, index) => (
                        <React.Fragment key={question.id}>
                          <TableRow
                            draggable
                            onDragStart={(e) => handleDragStart(e, question)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => {
                              e.preventDefault();
                              handleRowHover(e, 'Sin mesa', question, false);
                            }}
                            onDrop={handleDrop}
                            className={`cursor-move ${draggedQuestion?.id === question.id ? 'opacity-50' : ''}`}
                            style={{ cursor: 'grab' }}
                          >
                            {showImages ? (
                              <TableCell>
                                {question.image_id ? (
                                  <ImageComponent image_id={question.image_id} />
                                ) : (
                                  'Sin imagen asociada'
                                )}
                              </TableCell>
                            ) : null}
                            <TableCell>{question.content}</TableCell>
                            <TableCell>
                              {question.question_type === 'ALTERNATIVAS'
                                ? question.possible_answers[question.correct_answer]
                                : question.question_type === 'V/F'
                                  ? correctAnswerVF[question.correct_answer]
                                  : question.possible_answers.join(' | ')}
                            </TableCell>
                            <TableCell>{question.question_type}</TableCell>
                            <TableCell>{question.time_to_answer}</TableCell>
                            <TableCell>{question.table}</TableCell>
                            <TableCell>{question.order}</TableCell>
                            <TableCell>
                              <div className='flex w-full justify-between gap-2 items-center'>
                                <TableButton
                                  variant={'edit'}
                                  onClick={() => {
                                    setQuestionToEdit(question);
                                    setMethod('PATCH');
                                    setOpen(true);
                                  }}>
                                  <EditOutlinedIcon />
                                </TableButton>
                                <TableButton variant={'delete'} onClick={() => handleDelete(question.id)}>
                                  <DeleteOutlinedIcon color='red' />
                                </TableButton>
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* Drop line after question */}
                          <tr>
                            <td colSpan={showImages ? 8 : 7} className="p-0">
                              <div
                                className={`h-1 transition-all ${visibleLineId === `table-Sin mesa-line-${index + 1}`
                                  ? 'bg-blue-500 shadow-lg'
                                  : 'bg-transparent'
                                  }`}
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                  </React.Fragment>
                </>
              )}
            </TableBody>
          </Table>

        </div>
      </div>
    </div>
  );
};

export default EvaluationQuestionsTable;
